import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Booking, ParentChildProfile, PaymentMethod, PublicEvent, clubsBookingApi } from '@/api/clubs-booking';
import {
  getPublicCart,
  PublicCartItem,
  getPublicCartItemsForEvent,
  removePublicCartItem,
  removePublicCartItemForEvent,
  savePublicCart,
} from '@/utils/publicCart';
import PublicClubHeader from '@/components/public/PublicClubHeader';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { auth } from '@/store/authStore';
import { formatCurrencyFromCents } from '@/utils/currency';
import { getClubSiteHref } from '@/utils/publicSite';

const getSessionBookingCutoff = (session: NonNullable<PublicEvent['sessions']>[number]) => {
  const start = new Date(session.startDateTime);
  const cutoffHours = typeof session.bookingCutoffHours === 'number' ? session.bookingCutoffHours : undefined;
  if (cutoffHours !== undefined && Number.isFinite(cutoffHours) && cutoffHours >= 0) {
    return new Date(start.getTime() - (cutoffHours * 60 * 60 * 1000));
  }
  return start;
};

const isSessionBookableNow = (session: NonNullable<PublicEvent['sessions']>[number]) => {
  if (session.bookingEnabled === false) return false;
  const cutoff = getSessionBookingCutoff(session);
  if (Number.isNaN(cutoff.getTime())) return false;
  return Date.now() < cutoff.getTime();
};

const isSessionFull = (session: NonNullable<PublicEvent['sessions']>[number]) =>
  session.capacity >= 0 && typeof session.spotsRemaining === 'number' && session.spotsRemaining <= 0;

const isSessionBookableForPurchase = (session: NonNullable<PublicEvent['sessions']>[number]) =>
  isSessionBookableNow(session) && !isSessionFull(session);

type CheckoutChild = {
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

const getChildDisplayName = (child: Partial<CheckoutChild>) =>
  [String(child.firstName || '').trim(), String(child.lastName || '').trim()].filter(Boolean).join(' ').trim();

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const mapProfileChildrenToCheckout = (children: ParentChildProfile[] | undefined, fallback: CheckoutChild[]) => {
  if (!children?.length) return fallback;
  return children.map((child) => ({
    id: child.id,
    firstName: child.firstName || '',
    lastName: child.lastName || '',
    dateOfBirth: child.dateOfBirth || '',
  }));
};

const PublicBookingCheckoutPage = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasClientValidationError, setHasClientValidationError] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [cartItems, setCartItems] = useState<PublicCartItem[]>([]);
  const [existingParentEventBookings, setExistingParentEventBookings] = useState<Array<Booking & { event?: any }>>([]);
  const [checkoutMode, setCheckoutMode] = useState<'guest' | 'login'>('guest');
  const [isParentLoggedIn, setIsParentLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const [form, setForm] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    children: [{ firstName: '', lastName: '', dateOfBirth: '' }] as CheckoutChild[],
    discountCode: '',
    paymentMethod: 'card' as PaymentMethod,
    notes: '',
    addOnSelections: {} as Record<string, Record<number, string[]>>,
    ticketSelections: {} as Record<string, Record<number, string[]>>,
  });

  useEffect(() => {
    const user = auth.getUser();
    if (user?.isAuthenticated && user.role === 'parent') {
      setCheckoutMode('login');
      setIsParentLoggedIn(true);
      setForm((prev) => ({
        ...prev,
        parentName: user.name || prev.parentName,
        parentEmail: user.email || prev.parentEmail,
      }));
      void (async () => {
        try {
          const profile = await clubsBookingApi.getParentProfile();
          setForm((prev) => ({
            ...prev,
            parentName: profile.name || prev.parentName,
            parentEmail: profile.email || prev.parentEmail,
            parentPhone: profile.phone || prev.parentPhone,
            children: mapProfileChildrenToCheckout(profile.children, prev.children),
          }));
        } catch {
          // non-blocking; checkout still works with current form values
        }
      })();
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!eventSlug) return;
      setLoading(true);
      setError('');
      try {
        const data = await clubsBookingApi.getPublicEvent(eventSlug);
        setEvent(data);
        const eventPaymentMethods = Array.from(
          new Set((data.paymentMethods || ['card', 'cash']).filter((method) => method === 'card' || method === 'cash')),
        ) as PaymentMethod[];
        if (eventPaymentMethods.length) {
          setForm((prev) => ({
            ...prev,
            paymentMethod: eventPaymentMethods.includes('card')
              ? 'card'
              : eventPaymentMethods[0],
          }));
        }
        const existingItems = getPublicCartItemsForEvent(data._id);
        const sessions = data.sessions || [];
        const sessionsById = new Map(sessions.map((session) => [session.id, session]));
        const sanitizedItems = existingItems
          .map((item) => {
            const ticket = (data.ticketTypes || []).find((entry) => entry.id === item.ticketTypeId);
            if (!ticket) return null;
            const allowedSessionIds = new Set(ticket.mode === 'specific_sessions' ? (ticket.sessionIds || []) : sessions.map((session) => session.id));
            const candidateSessionIds = item.sessionIds.length > 0
              ? item.sessionIds
              : (ticket.mode === 'all_sessions' ? sessions.map((session) => session.id) : []);
            const validSessionIds = candidateSessionIds.filter((sessionId) => {
              if (!allowedSessionIds.has(sessionId)) return false;
              const session = sessionsById.get(sessionId);
              if (!session) return false;
              return isSessionBookableForPurchase(session);
            });
            if (!validSessionIds.length) return null;
            return {
              ...item,
              sessionIds: Array.from(new Set(validSessionIds)),
            };
          })
          .filter(Boolean) as PublicCartItem[];

        const hadSanitizationChanges =
          sanitizedItems.length !== existingItems.length ||
          sanitizedItems.some((item, idx) => {
            const original = existingItems[idx];
            if (!original) return true;
            if (item.id !== original.id) return true;
            if (item.sessionIds.length !== original.sessionIds.length) return true;
            return item.sessionIds.some((sessionId) => !original.sessionIds.includes(sessionId));
          });

        if (hadSanitizationChanges) {
          const currentCart = getPublicCart();
          savePublicCart([
            ...currentCart.filter((entry) => entry.eventId !== data._id),
            ...sanitizedItems,
          ]);
          setMessage('Some cart sessions are no longer bookable and were removed.');
        }

        setCartItems(sanitizedItems);
        if (!sanitizedItems.length) {
          setError('Your cart is empty for this activity. Please add tickets first.');
        }
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load this checkout');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [eventSlug]);

  useEffect(() => {
    const loadExistingParentBookings = async () => {
      if (!isParentLoggedIn || !event?._id) {
        setExistingParentEventBookings([]);
        return;
      }
      try {
        const bookings = await clubsBookingApi.listParentBookings();
        setExistingParentEventBookings(bookings.filter((booking) => booking.eventId === event._id));
      } catch {
        setExistingParentEventBookings([]);
      }
    };
    void loadExistingParentBookings();
  }, [isParentLoggedIn, event?._id]);

  const allSelectedSessionIds = useMemo(() => {
    return Array.from(new Set(cartItems.flatMap((item) => item.sessionIds)));
  }, [cartItems]);

  const getTicketSelectionsForItem = (item: PublicCartItem) => {
    const existing = form.ticketSelections[item.id] || {};
    return form.children.map((_, childIndex) => existing[childIndex] || item.sessionIds);
  };

  const granularCartItems = useMemo(() => {
    return cartItems.flatMap((item) => {
      const selections = getTicketSelectionsForItem(item);
      const grouped = new Map<string, { sessionIds: string[]; childIndexes: number[] }>();
      selections.forEach((sessionIds, childIndex) => {
        const validSessionIds = Array.from(new Set((sessionIds || []).filter((sessionId) => item.sessionIds.includes(sessionId))));
        if (!validSessionIds.length) return;
        const key = validSessionIds.slice().sort().join('|');
        const existing = grouped.get(key) || { sessionIds: validSessionIds, childIndexes: [] };
        existing.childIndexes.push(childIndex);
        grouped.set(key, existing);
      });
      return Array.from(grouped.values()).map((group) => ({
        ticketTypeId: item.ticketTypeId,
        selectedSessionIds: group.sessionIds,
        childIndexes: group.childIndexes,
        quantity: 1,
      }));
    });
  }, [cartItems, form.children, form.ticketSelections]);

  const selectedSessionIdsByChild = useMemo(() => {
    const selections: Record<number, string[]> = {};
    granularCartItems.forEach((item) => {
      (item.childIndexes || []).forEach((childIndex) => {
        selections[childIndex] = Array.from(new Set([
          ...(selections[childIndex] || []),
          ...(item.selectedSessionIds || []),
        ]));
      });
    });
    return selections;
  }, [granularCartItems]);

  const availablePaymentMethods = useMemo(() => {
    if (!event) return ['card', 'cash'] as PaymentMethod[];
    const methods = Array.from(
      new Set((event.paymentMethods || ['card', 'cash']).filter((method) => method === 'card' || method === 'cash')),
    ) as PaymentMethod[];
    return methods;
  }, [event]);

  const ticketSubtotalCents = useMemo(() => {
    if (!event) return 0;
    return granularCartItems.reduce((sum, item) => {
      const ticket = (event.ticketTypes || []).find((entry) => entry.id === item.ticketTypeId);
      if (!ticket) return sum;
      const sessionMultiplier = ticket.mode === 'specific_sessions' ? Math.max(1, item.selectedSessionIds?.length || 0) : 1;
      return sum + (ticket.priceCents * sessionMultiplier * Math.max(1, item.childIndexes?.length || 0));
    }, 0);
  }, [event, granularCartItems]);

  const automaticDiscountCents = useMemo(() => {
    if (!event?.automaticDiscounts?.length) return 0;
    const requestedSpots = Math.max(1, new Set(granularCartItems.flatMap((item) => item.childIndexes || [])).size || form.children.length);
    const uniqueSessionCount = allSelectedSessionIds.length;
    let remainingTicketCents = Math.max(0, Math.round(ticketSubtotalCents));
    let totalDiscount = 0;

    event.automaticDiscounts.forEach((rule) => {
      if (remainingTicketCents <= 0) return;
      let discountCents = 0;

      if (rule.type === 'multi_attendee') {
        const minAttendees = Number(rule.minAttendees || 2);
        if (requestedSpots < minAttendees) return;
        const discountedAttendeeCount = Math.max(0, requestedSpots - (minAttendees - 1));
        const amountValue = Number(rule.amountValue || 0);

        if (rule.amountType === 'fixed') {
          const averagePerAttendeeCents = Math.max(0, Math.round(remainingTicketCents / Math.max(1, requestedSpots)));
          const perAttendeeDiscountCents = Math.min(Math.max(0, Math.round(amountValue)), averagePerAttendeeCents);
          discountCents = Math.max(0, perAttendeeDiscountCents * discountedAttendeeCount);
        } else {
          const eligibleBaseCents = Math.round((remainingTicketCents * discountedAttendeeCount) / Math.max(1, requestedSpots));
          discountCents = Math.max(0, Math.round((eligibleBaseCents * amountValue) / 100));
        }
      } else if (rule.type === 'multi_purchase') {
        const minSessions = Number(rule.minSessionsPerAttendee || 2);
        if (uniqueSessionCount < minSessions) return;
        const amountValue = Number(rule.amountValue || 0);
        if (rule.amountType === 'fixed') {
          discountCents = Math.max(0, Math.round(amountValue * requestedSpots));
        } else {
          discountCents = Math.max(0, Math.round((remainingTicketCents * amountValue) / 100));
        }
      }

      discountCents = Math.min(discountCents, remainingTicketCents);
      if (discountCents <= 0) return;

      remainingTicketCents -= discountCents;
      totalDiscount += discountCents;
    });

    return totalDiscount;
  }, [event, form.children.length, granularCartItems, allSelectedSessionIds.length, ticketSubtotalCents]);

  const addOnTotalCents = useMemo(() => {
    if (!event) return 0;
    return (event.addOns || []).reduce((sum, addOn) => {
      const childSelections = form.addOnSelections[addOn.id] || {};
      return sum + Object.entries(childSelections).reduce((childSum, [childIndex, sessions]) => {
        const selectedSessionIdsForChild = selectedSessionIdsByChild[Number(childIndex)] || [];
        const validSessionCount = (sessions || []).filter((sessionId) => selectedSessionIdsForChild.includes(sessionId)).length;
        return childSum + (addOn.priceCents * validSessionCount);
      }, 0);
    }, 0);
  }, [event, form.addOnSelections, selectedSessionIdsByChild]);

  const totalDueCents = useMemo(() => {
    return Math.max(0, ticketSubtotalCents - automaticDiscountCents + addOnTotalCents);
  }, [ticketSubtotalCents, automaticDiscountCents, addOnTotalCents]);

  const totalDisplay = useMemo(() => {
    if (!event) return '';
    return formatCurrencyFromCents(totalDueCents, event.currency);
  }, [event, totalDueCents]);

  const duplicateBookingWarnings = useMemo(() => {
    if (!isParentLoggedIn || !event || !allSelectedSessionIds.length) return [] as Array<{ childName: string; sessionLabels: string[] }>;

    const normalize = (value: string) => value.trim().toLowerCase();
    const selectedChildren = form.children
      .map((child) => {
        const displayName = getChildDisplayName(child);
        const fallbackIdentity = `${normalize(child.firstName)}::${normalize(child.lastName)}::${child.dateOfBirth || ''}`;
        return {
          rawName: displayName || 'Child',
          key: child.id ? `id::${child.id}` : `name::${fallbackIdentity}`,
        };
      })
      .filter((child) => child.key && child.key !== 'name::::');

    if (!selectedChildren.length) return [] as Array<{ childName: string; sessionLabels: string[] }>;

    const selectedSessionSet = new Set(allSelectedSessionIds);
    const existingChildSessionPairs = new Set<string>();

    existingParentEventBookings.forEach((booking) => {
      if (booking.attendanceStatus === 'not_coming') return;
      const relevantSessionIds = (booking.sessionIds || []).filter((sessionId) => selectedSessionSet.has(sessionId));
      if (!relevantSessionIds.length) return;

      const bookingChildren = Array.isArray(booking.children) && booking.children.length
        ? booking.children.map((child) => ({
            key: child.id
              ? `id::${child.id}`
              : `name::${normalize(child.firstName)}::${normalize(child.lastName)}::${child.dateOfBirth || ''}`,
          }))
        : [];

      bookingChildren
        .map((child) => child.key)
        .filter(Boolean)
        .forEach((childKey) => {
          relevantSessionIds.forEach((sessionId) => {
            existingChildSessionPairs.add(`${childKey}::${sessionId}`);
          });
        });
    });

    const sessionLabel = (sessionId: string) => {
      const session = (event.sessions || []).find((entry) => entry.id === sessionId);
      if (!session) return sessionId;
      return `${new Date(session.startDateTime).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${new Date(session.startDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    };

    return selectedChildren
      .map((child) => {
        const duplicateSessionLabels = allSelectedSessionIds
          .filter((sessionId) => existingChildSessionPairs.has(`${child.key}::${sessionId}`))
          .map(sessionLabel);
        return {
          childName: child.rawName,
          sessionLabels: Array.from(new Set(duplicateSessionLabels)),
        };
      })
      .filter((entry) => entry.sessionLabels.length > 0);
  }, [isParentLoggedIn, event, allSelectedSessionIds, form.children, existingParentEventBookings]);

  const otherActivitiesInCartCount = useMemo(() => {
    if (!event?._id) return 0;
    const otherEventIds = new Set(
      getPublicCart()
        .map((item) => item.eventId)
        .filter((eventId) => eventId && eventId !== event._id),
    );
    return otherEventIds.size;
  }, [event?._id, cartItems]);
  const checkoutBackHref = otherActivitiesInCartCount > 0 ? '/cart' : (event?._id ? `/book/${event._id}` : '/clubs');
  const checkoutBackLabel = otherActivitiesInCartCount > 0 ? 'Back to cart' : 'Back to activity';

  const formatMoney = (amountCents: number) => {
    if (!event) return '';
    return formatCurrencyFromCents(amountCents, event.currency);
  };

  const formatSessionSummary = useCallback((sessionId: string) => {
    const session = (event?.sessions || []).find((entry) => entry.id === sessionId);
    if (!session) return sessionId;
    return `${new Date(session.startDateTime).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${new Date(session.startDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }, [event?.sessions]);

  const ticketBreakdown = useMemo(() => {
    if (!event) return [];
    return granularCartItems.flatMap((item, itemIndex) => {
      const ticket = (event.ticketTypes || []).find((entry) => entry.id === item.ticketTypeId);
      if (!ticket) return [];
      const attendeeCount = Math.max(1, item.childIndexes?.length || 0);
      const attendeeLabel = `${attendeeCount} attendee${attendeeCount === 1 ? '' : 's'}`;

      if (ticket.mode === 'specific_sessions') {
        return (item.selectedSessionIds || []).map((sessionId) => ({
          key: `${itemIndex}-${ticket.id}-${sessionId}`,
          title: ticket.name,
          subtitle: formatSessionSummary(sessionId),
          detail: `${attendeeLabel} x ${formatCurrencyFromCents(ticket.priceCents, event.currency)}`,
          amountCents: ticket.priceCents * attendeeCount,
        }));
      }

      const sessionCount = Math.max(1, item.selectedSessionIds?.length || 0);
      return [{
        key: `${itemIndex}-${ticket.id}-all`,
        title: ticket.name,
        subtitle: `${sessionCount} session${sessionCount === 1 ? '' : 's'} included`,
        detail: `${attendeeLabel} x ${formatCurrencyFromCents(ticket.priceCents, event.currency)}`,
        amountCents: ticket.priceCents * attendeeCount,
      }];
    });
  }, [event, granularCartItems, formatSessionSummary]);

  const addOnBreakdown = useMemo(() => {
    if (!event) return [];
    return (event.addOns || []).flatMap((addOn) => {
      const childSelections = form.addOnSelections[addOn.id] || {};
      const countsBySession = new Map<string, number>();
      Object.entries(childSelections).forEach(([childIndex, sessionIds]) => {
        const selectedSessionIdsForChild = selectedSessionIdsByChild[Number(childIndex)] || [];
        (sessionIds || []).forEach((sessionId) => {
          if (!selectedSessionIdsForChild.includes(sessionId)) return;
          countsBySession.set(sessionId, (countsBySession.get(sessionId) || 0) + 1);
        });
      });

      return Array.from(countsBySession.entries()).map(([sessionId, attendeeCount]) => ({
        key: `${addOn.id}-${sessionId}`,
        title: addOn.name,
        subtitle: formatSessionSummary(sessionId),
        detail: `${attendeeCount} attendee${attendeeCount === 1 ? '' : 's'} x ${formatCurrencyFromCents(addOn.priceCents, event.currency)}`,
        amountCents: addOn.priceCents * attendeeCount,
      }));
    });
  }, [event, form.addOnSelections, selectedSessionIdsByChild, formatSessionSummary]);

  const selectedSessionSpotsRemaining = useMemo(() => {
    if (!event || !allSelectedSessionIds.length) return event?.spotsRemaining ?? 0;
    const selectedSessions = (event.sessions || []).filter((session) => allSelectedSessionIds.includes(session.id));
    const finiteRemaining = selectedSessions
      .filter((session) => session.capacity >= 0)
      .map((session) => Number(session.spotsRemaining ?? 0));
    if (!finiteRemaining.length) return -1;
    return Math.min(...finiteRemaining);
  }, [event, allSelectedSessionIds]);

  const isFreeEvent = totalDueCents === 0;
  const hasUnlimitedSpots = selectedSessionSpotsRemaining < 0;
  const isFullyBooked = !!event && !hasUnlimitedSpots && selectedSessionSpotsRemaining <= 0;
  const exceedsSpots = useMemo(() => {
    if (!event) return false;
    const demandBySession = new Map<string, number>();
    granularCartItems.forEach((item) => {
      (item.selectedSessionIds || []).forEach((sessionId) => {
        demandBySession.set(sessionId, (demandBySession.get(sessionId) || 0) + Math.max(1, item.childIndexes?.length || 0));
      });
    });
    return Array.from(demandBySession.entries()).some(([sessionId, demand]) => {
      const session = (event.sessions || []).find((entry) => entry.id === sessionId);
      if (!session || session.capacity < 0) return false;
      return demand > Number(session.spotsRemaining ?? 0);
    });
  }, [event, granularCartItems]);
  const canContinueCheckout = checkoutMode === 'guest' || isParentLoggedIn;
  const handleUseDifferentAccount = () => {
    auth.clearAuth();
    clubsBookingApi.clearAuthToken();
    setIsParentLoggedIn(false);
    setCheckoutMode('guest');
    setLoginForm({ email: '', password: '' });
    setAuthError('');
  };

  const getClientValidationError = useCallback(() => {
    if (checkoutMode === 'login' && !isParentLoggedIn) {
      return 'Please login to your parent account, or switch to guest checkout.';
    }
    if (!isFreeEvent && availablePaymentMethods.length === 0) {
      return 'No payment methods are currently available for this activity. Please contact the club.';
    }
    if (!isFreeEvent && !availablePaymentMethods.includes(form.paymentMethod)) {
      return 'Selected payment method is not currently available for this activity.';
    }
    if (!form.parentName.trim()) {
      return 'Please enter the parent name.';
    }
    if (!form.parentEmail.trim()) {
      return 'Please enter the parent email address.';
    }
    if (!isValidEmail(form.parentEmail)) {
      return 'Please enter a valid parent email address.';
    }

    const normalizedChildren = form.children
      .map((child) => ({
        firstName: child.firstName.trim(),
        lastName: child.lastName.trim(),
        dateOfBirth: child.dateOfBirth,
      }))
      .filter((child) => child.firstName || child.lastName || child.dateOfBirth);

    if (!normalizedChildren.length) {
      return 'Please add at least one child.';
    }
    if (normalizedChildren.some((child) => !child.firstName || !child.lastName || !child.dateOfBirth)) {
      return 'Each child needs a first name, last name, and date of birth.';
    }
    if (!granularCartItems.length || granularCartItems.every((item) => !item.childIndexes.length || !item.selectedSessionIds.length)) {
      return 'Please select at least one session for at least one attendee.';
    }
    if (isFullyBooked) {
      return 'This activity is fully booked.';
    }
    if (exceedsSpots) {
      return 'There are not enough spaces left for the selected attendees and sessions. Please remove an attendee or untick some sessions.';
    }
    return '';
  }, [
    checkoutMode,
    isParentLoggedIn,
    isFreeEvent,
    availablePaymentMethods,
    form.paymentMethod,
    form.parentName,
    form.parentEmail,
    form.children,
    granularCartItems,
    isFullyBooked,
    exceedsSpots,
  ]);

  useEffect(() => {
    if (!hasClientValidationError) return;
    setError(getClientValidationError());
  }, [hasClientValidationError, getClientValidationError]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!event || !cartItems.length) return;
    const normalizedChildren = form.children
      .map((child) => ({
        id: child.id,
        firstName: child.firstName.trim(),
        lastName: child.lastName.trim(),
        dateOfBirth: child.dateOfBirth,
      }))
      .filter((child) => child.firstName || child.lastName || child.dateOfBirth);

    const clientValidationError = getClientValidationError();
    if (clientValidationError) {
      setHasClientValidationError(true);
      setError(clientValidationError);
      return;
    }

    setSubmitting(true);
    setHasClientValidationError(false);
    setError('');
    setMessage('');
    try {
      const result = await clubsBookingApi.createPublicBooking(event._id, {
        parentName: form.parentName,
        parentEmail: form.parentEmail,
        parentPhone: form.parentPhone || undefined,
        children: normalizedChildren,
        cartItems: granularCartItems.map((item) => ({
          ticketTypeId: item.ticketTypeId,
          selectedSessionIds: item.selectedSessionIds,
          childIndexes: item.childIndexes,
          addOnSelections: Object.entries(form.addOnSelections)
            .flatMap(([addOnId, childSelections]) => Object.entries(childSelections).map(([childIndex, sessionIds]) => ({
              addOnId,
              childIndexes: [Number(childIndex)],
              sessionIds: (sessionIds || []).filter((sessionId) => (item.selectedSessionIds || []).includes(sessionId)),
            })))
            .filter((selection) => selection.sessionIds.length > 0 && (item.childIndexes || []).includes(selection.childIndexes[0])),
        })),
        checkoutMode,
        paymentMethod: isFreeEvent ? undefined : form.paymentMethod,
        notes: form.notes || undefined,
        discountCode: form.discountCode.trim() || undefined,
      });

      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }

      removePublicCartItemForEvent(event._id);
      setCartItems([]);
      navigate(`/book/${event._id}/completed?booking_id=${result.booking._id}`);
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onParentLogin = async (e: FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    setError('');
    try {
      const response = await clubsBookingApi.parentLogin(loginForm.email, loginForm.password);
      auth.setAuth(response.accessToken, response.id, response.name, loginForm.email, 'parent');
      clubsBookingApi.setAuthToken(response.accessToken);
      setIsParentLoggedIn(true);
      const profile = await clubsBookingApi.getParentProfile();
      setForm((prev) => ({
        ...prev,
        parentName: profile.name || response.name || prev.parentName,
        parentEmail: profile.email || loginForm.email,
        parentPhone: profile.phone || prev.parentPhone,
        children: mapProfileChildrenToCheckout(profile.children, prev.children),
      }));
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setAuthError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Parent login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  if (loading) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6">Loading checkout...</main>;
  }

  if (error && !event) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6 text-red-700">{error}</main>;
  }

  if (!event) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6">Activity not found.</main>;
  }

  if (!cartItems.length) {
    const emptyCartContent = (
      <section className="px-3 pb-8 pt-6 sm:px-4 md:px-8">
        <div className="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <p className="text-sm font-medium uppercase tracking-wide text-slate-500">Checkout</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">Your cart is empty</h1>
          <p className="mt-3 text-sm text-slate-600 sm:text-base">
            Add tickets for <span className="font-medium text-slate-800">{event.title}</span> before continuing to checkout.
          </p>
          {error && <p className="mt-3 text-sm text-amber-700">{error}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/book/${event._id}`}
              className="inline-flex items-center rounded-lg bg-[#1F8F5F] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#1F8F5F]"
            >
              Back to activity
            </Link>
            {event.clubProfile?.publicSlug && (
              <a
                href={getClubSiteHref(event.clubProfile.publicSlug)}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Browse club activities
              </a>
            )}
          </div>
        </div>
      </section>
    );

    if (isParentLoggedIn) {
      return (
        <ParentPortalShell title="Checkout" subtitle={event.title} showHeaderCard={false}>
          {emptyCartContent}
        </ParentPortalShell>
      );
    }

    return (
      <main className="min-h-screen bg-[#f3f4f8]">
        <PublicClubHeader
          clubName={event.clubProfile?.clubName || 'After-school club'}
          clubSlug={event.clubProfile?.publicSlug}
          clubDescription={event.clubProfile?.description}
          logoUrl={event.clubProfile?.logoUrl}
          showClubTitle={event.clubProfile?.showClubTitle !== false}
          cartHref="/cart"
        />
        {emptyCartContent}
      </main>
    );
  }

  const pageContent = (
      <div className="grid w-full gap-6 px-3 pb-8 pt-6 sm:px-4 md:px-8 lg:grid-cols-[1fr_360px]">
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-slate-900">Checkout</h1>
            <div className="flex items-center gap-3">
              {event.clubProfile?.publicSlug && <a href={getClubSiteHref(event.clubProfile.publicSlug)} className="text-sm text-slate-600 hover:underline">Back to club</a>}
              <Link to={checkoutBackHref} data-testid="checkout-back-button" className="text-sm text-[#1F8F5F] hover:underline">
                {checkoutBackLabel}
              </Link>
            </div>
          </div>

          {otherActivitiesInCartCount > 0 && (
            <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
              You also have tickets for {otherActivitiesInCartCount} other activit{otherActivitiesInCartCount === 1 ? 'y' : 'ies'} in your cart.
              Each activity is checked out separately and creates a separate booking reference.
              <Link to={`/cart?backTo=${encodeURIComponent(`/book/${event._id}/checkout`)}`} className="ml-1 font-medium underline">View full cart</Link>
            </div>
          )}

          <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-700">Cart items</p>
            <div className="mt-3 space-y-3">
              {cartItems.map((item) => {
                const ticket = (event.ticketTypes || []).find((entry) => entry.id === item.ticketTypeId);
                const selectedSessions = item.sessionIds
                  .map((sessionId) => (event.sessions || []).find((entry) => entry.id === sessionId))
                  .filter(Boolean) as NonNullable<PublicEvent['sessions']>[number][];
                const linePriceCents = ticket
                  ? ticket.priceCents * (ticket.mode === 'specific_sessions' ? Math.max(1, selectedSessions.length) : 1)
                  : 0;
                return (
                  <div key={item.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium text-slate-900">{ticket?.name || 'Ticket'} <span className="text-slate-600">({formatMoney(linePriceCents)})</span></p>
                      <p className="text-slate-600">{item.sessionIds.length} session{item.sessionIds.length === 1 ? '' : 's'} selected</p>
                      {selectedSessions.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-slate-500">
                          {selectedSessions.map((session) => (
                            <li key={session.id}>
                              {new Date(session.startDateTime).toLocaleString('en-GB')} - {new Date(session.endDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button type="button" className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50" onClick={() => {
                      const updated = removePublicCartItem(item.id);
                      setCartItems(updated.filter((entry) => entry.eventId === event._id));
                    }}>Remove</button>
                  </div>
                );
              })}
            </div>
            <button type="button" className="mt-3 text-sm text-[#1F8F5F] hover:underline" onClick={() => {
              removePublicCartItemForEvent(event._id);
              setCartItems([]);
            }}>Clear all</button>
          </div>

          {!!event.automaticDiscounts?.length && (
            <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm font-medium text-emerald-900">Automatic discounts available</p>
                  <div className="mt-2 space-y-1 text-sm text-emerald-800">
                    {event.automaticDiscounts.map((discount) => (
                      <p key={discount.id}>{discount.summary}</p>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-emerald-700">
                    Discounts shown below are applied to ticket totals automatically.
                  </p>
                </div>
              )}

          {!isParentLoggedIn && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-700">Checkout type</p>
              <div className="mt-3 inline-flex rounded-lg border border-slate-300 bg-white p-1">
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-sm ${checkoutMode === 'guest' ? 'bg-[#1F8F5F] text-white' : 'text-slate-700'}`}
                  onClick={() => {
                    setCheckoutMode('guest');
                    setAuthError('');
                    setError('');
                  }}
                >
                  Guest checkout
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-sm ${checkoutMode === 'login' ? 'bg-[#1F8F5F] text-white' : 'text-slate-700'}`}
                  onClick={() => {
                    setCheckoutMode('login');
                    setError('');
                  }}
                >
                  Login to account
                </button>
              </div>

              {checkoutMode === 'login' && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                  <form onSubmit={onParentLogin} className="space-y-2">
                    <label className="block text-sm text-slate-600">Parent email<input type="email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={loginForm.email} onChange={(e) => setLoginForm((v) => ({ ...v, email: e.target.value }))} required /></label>
                    <label className="block text-sm text-slate-600">Password<input type="password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={loginForm.password} onChange={(e) => setLoginForm((v) => ({ ...v, password: e.target.value }))} required /></label>
                    {authError && <p className="text-sm text-red-600">{authError}</p>}
                    <button type="submit" disabled={authLoading} className="rounded-lg bg-[#1F8F5F] px-3 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60">
                      {authLoading ? 'Logging in...' : 'Login and continue'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}

          {!canContinueCheckout && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Log in to your parent account to continue to attendee details and payment.
            </div>
          )}

          {canContinueCheckout && (
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            {checkoutMode === 'guest' && !isParentLoggedIn ? (
              <>
                <label className="block text-sm text-slate-600">Parent name<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.parentName} onChange={(e) => setForm((v) => ({ ...v, parentName: e.target.value }))} /></label>
                <label className="block text-sm text-slate-600">Parent email<input type="email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.parentEmail} onChange={(e) => setForm((v) => ({ ...v, parentEmail: e.target.value }))} /></label>
              </>
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-emerald-900">Parent account</p>
                    <p className="mt-1 text-emerald-800">{form.parentName || 'Parent'} ({form.parentEmail})</p>
                  </div>
                  {isParentLoggedIn && (
                    <button
                      type="button"
                      className="text-xs text-emerald-800 underline hover:text-emerald-950"
                      onClick={handleUseDifferentAccount}
                    >
                      Use different account
                    </button>
                  )}
                </div>
              </div>
            )}
            <label className="block text-sm text-slate-600">Parent phone (optional)<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.parentPhone} onChange={(e) => setForm((v) => ({ ...v, parentPhone: e.target.value }))} /></label>

            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm font-medium text-slate-700">Attendee details</p>
              {form.children.map((child, index) => (
                <div key={child.id || index} className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px_auto]">
                  <input
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="First name"
                    value={child.firstName}
                    onChange={(e) => setForm((v) => ({
                      ...v,
                      children: v.children.map((item, idx) => (idx === index ? { ...item, firstName: e.target.value } : item)),
                    }))}
                  />
                  <input
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Last name"
                    value={child.lastName}
                    onChange={(e) => setForm((v) => ({
                      ...v,
                      children: v.children.map((item, idx) => (idx === index ? { ...item, lastName: e.target.value } : item)),
                    }))}
                  />
                  <input
                    type="date"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={child.dateOfBirth}
                    onChange={(e) => setForm((v) => ({
                      ...v,
                      children: v.children.map((item, idx) => (idx === index ? { ...item, dateOfBirth: e.target.value } : item)),
                    }))}
                  />
                  <button
                    type="button"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50"
                    disabled={form.children.length === 1}
                    onClick={() =>
                      setForm((v) => {
                        const nextChildren = v.children.filter((_, idx) => idx !== index);
                        const nextAddOnSelections = Object.fromEntries(
                          Object.entries(v.addOnSelections).map(([addOnId, childSelections]) => {
                            const remapped: Record<number, string[]> = {};
                            Object.entries(childSelections || {}).forEach(([childIndex, sessionIds]) => {
                              const parsed = Number(childIndex);
                              if (parsed === index) return;
                              remapped[parsed > index ? parsed - 1 : parsed] = sessionIds;
                            });
                            return [addOnId, remapped];
                          }),
                        );
                        const nextTicketSelections = Object.fromEntries(
                          Object.entries(v.ticketSelections).map(([itemId, childSelections]) => {
                            const remapped: Record<number, string[]> = {};
                            Object.entries(childSelections || {}).forEach(([childIndex, sessionIds]) => {
                              const parsed = Number(childIndex);
                              if (parsed === index) return;
                              remapped[parsed > index ? parsed - 1 : parsed] = sessionIds;
                            });
                            return [itemId, remapped];
                          }),
                        );
                        return {
                          ...v,
                          children: nextChildren,
                          addOnSelections: nextAddOnSelections,
                          ticketSelections: nextTicketSelections,
                        };
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50" disabled={!hasUnlimitedSpots && form.children.length >= selectedSessionSpotsRemaining} onClick={() => setForm((v) => ({ ...v, children: [...v.children, { firstName: '', lastName: '', dateOfBirth: '' }] }))}>Add another child</button>
            </div>

            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm font-medium text-slate-700">Ticket sessions by attendee</p>
              {cartItems.map((item) => {
                const ticket = (event.ticketTypes || []).find((entry) => entry.id === item.ticketTypeId);
                return (
                  <div key={item.id} className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-sm font-semibold text-slate-900">{ticket?.name || 'Ticket'}</p>
                    <div className="mt-3 space-y-3">
                      {form.children.map((child, childIndex) => (
                        <div key={`${item.id}-${childIndex}`} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                          <p className="mb-2 text-xs font-medium text-slate-600">
                            {getChildDisplayName(child) || `Child ${childIndex + 1}`}
                          </p>
                          <div className="grid gap-1">
                            {item.sessionIds.map((sessionId) => {
                              const session = (event.sessions || []).find((entry) => entry.id === sessionId);
                              const selectedForChild = form.ticketSelections[item.id]?.[childIndex] || item.sessionIds;
                              const checked = selectedForChild.includes(sessionId);
                              return (
                                <label key={`${item.id}-${childIndex}-${sessionId}`} className="flex items-center gap-2 text-sm text-slate-700">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => setForm((prev) => {
                                      const existingItem = prev.ticketSelections[item.id] || {};
                                      const currentForChild = existingItem[childIndex] || item.sessionIds;
                                      const nextForChild = e.target.checked
                                        ? Array.from(new Set([...currentForChild, sessionId]))
                                        : currentForChild.filter((id) => id !== sessionId);
                                      return {
                                        ...prev,
                                        ticketSelections: {
                                          ...prev.ticketSelections,
                                          [item.id]: {
                                            ...existingItem,
                                            [childIndex]: nextForChild,
                                          },
                                        },
                                      };
                                    })}
                                  />
                                  <span>{session ? new Date(session.startDateTime).toLocaleString('en-GB') : sessionId}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {!!event.addOns?.length && !!allSelectedSessionIds.length && (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-medium text-slate-700">Add-ons (optional)</p>
                {event.addOns.map((addOn) => (
                  <div key={addOn.id} className="rounded-lg border border-slate-200 bg-white p-2">
                        <p className="text-sm font-medium text-slate-900">{addOn.name} (+{formatCurrencyFromCents(addOn.priceCents, event.currency)} / session / child)</p>
                    <div className="mt-2 space-y-3">
                      {form.children.map((child, childIndex) => {
                        const sessionIdsForChild = selectedSessionIdsByChild[childIndex] || [];
                        if (!sessionIdsForChild.length) return null;
                        return (
                          <div key={`${addOn.id}-${childIndex}`} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                            <p className="mb-1 text-xs font-medium text-slate-600">
                              {getChildDisplayName(child) || `Child ${childIndex + 1}`}
                            </p>
                            <div className="grid gap-1">
                              {sessionIdsForChild.map((sessionId) => {
                                const session = (event.sessions || []).find((entry) => entry.id === sessionId);
                                const checked = (form.addOnSelections[addOn.id]?.[childIndex] || []).includes(sessionId);
                                return (
                                  <label key={`${addOn.id}-${childIndex}-${sessionId}`} className="flex items-center gap-2 text-sm text-slate-700">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) =>
                                        setForm((prev) => {
                                          const existingAddOn = prev.addOnSelections[addOn.id] || {};
                                          const currentForChild = existingAddOn[childIndex] || [];
                                          const nextForChild = e.target.checked
                                            ? Array.from(new Set([...currentForChild, sessionId]))
                                            : currentForChild.filter((id) => id !== sessionId);
                                          return {
                                            ...prev,
                                            addOnSelections: {
                                              ...prev.addOnSelections,
                                              [addOn.id]: {
                                                ...existingAddOn,
                                                [childIndex]: nextForChild,
                                              },
                                            },
                                          };
                                        })
                                      }
                                    />
                                    <span>{session ? new Date(session.startDateTime).toLocaleString('en-GB') : sessionId}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {availablePaymentMethods.length > 0 && (
              <label className="block text-sm text-slate-600">
                Payment method
                <select
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.paymentMethod}
                  onChange={(e) => setForm((v) => ({ ...v, paymentMethod: e.target.value as PaymentMethod }))}
                  disabled={availablePaymentMethods.length === 1}
                >
                  {availablePaymentMethods.includes('card') && <option value="card">Card</option>}
                  {availablePaymentMethods.includes('cash') && <option value="cash">Cash</option>}
                </select>
              </label>
            )}

            {availablePaymentMethods.includes(form.paymentMethod) && form.paymentMethod === 'cash' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {isFreeEvent
                  ? 'No payment is due for this booking. If any cash balance is due, it must be paid on the day.'
                  : 'Cash payments must be paid on the day.'}
              </div>
            )}

            {!isFreeEvent && availablePaymentMethods.length === 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                No payment methods are currently available for this activity. Please contact the club.
              </div>
            )}

            <label className="block text-sm text-slate-600">
              Discount code (optional)
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 uppercase"
                value={form.discountCode}
                onChange={(e) => setForm((v) => ({ ...v, discountCode: e.target.value.toUpperCase() }))}
                placeholder="Enter code"
              />
            </label>

            <label className="block text-sm text-slate-600">Notes (optional)<textarea className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" rows={3} value={form.notes} onChange={(e) => setForm((v) => ({ ...v, notes: e.target.value }))} /></label>

            {message && <p className="text-sm text-emerald-700">{message}</p>}
            {duplicateBookingWarnings.length > 0 && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-800">Duplicate booking warning</p>
                <p className="mt-1 text-sm text-red-700">
                  One or more children are already booked onto selected sessions. Please double-check before confirming.
                </p>
                <ul className="mt-2 space-y-1 text-sm text-red-700">
                  {duplicateBookingWarnings.map((warning) => (
                    <li key={warning.childName}>
                      <span className="font-medium">{warning.childName}:</span> {warning.sessionLabels.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button type="submit" disabled={submitting || cartItems.length === 0 || (!isFreeEvent && availablePaymentMethods.length === 0)} className="w-full rounded-lg bg-[#1F8F5F] px-4 py-2.5 font-medium text-white hover:bg-[#1F8F5F] disabled:cursor-not-allowed disabled:opacity-60">
              {submitting ? 'Submitting...' : (!isFreeEvent && availablePaymentMethods.length === 0) ? 'No payment methods available' : 'Confirm booking'}
            </button>
            {error && (
              <div className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-4 text-red-800 shadow-sm" role="alert" aria-live="assertive">
                <p className="text-base font-semibold">Please check the booking form</p>
                <p className="mt-1 text-sm">{error}</p>
              </div>
            )}
          </form>
          )}
        </section>

        <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-lg font-semibold text-slate-900">Your cart</h2>
          <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-900">{event.title}</p>
            <div className="space-y-2">
              {ticketBreakdown.map((line) => (
                <div key={line.key} className="flex items-start justify-between gap-3 border-t border-slate-200 pt-2 first:border-t-0 first:pt-0">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{line.title}</p>
                    <p className="text-xs text-slate-500">{line.subtitle}</p>
                    <p className="text-xs text-slate-600">{line.detail}</p>
                  </div>
                  <p className="shrink-0 font-medium text-slate-800">{formatMoney(line.amountCents)}</p>
                </div>
              ))}
            </div>
            {!!addOnBreakdown.length && (
              <div className="space-y-2 border-t border-slate-200 pt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add-ons</p>
                {addOnBreakdown.map((line) => (
                  <div key={line.key} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{line.title}</p>
                      <p className="text-xs text-slate-500">{line.subtitle}</p>
                      <p className="text-xs text-slate-600">{line.detail}</p>
                    </div>
                    <p className="shrink-0 font-medium text-slate-800">{formatMoney(line.amountCents)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-3 space-y-1 rounded-lg border border-[#D8F1E2] bg-[#F3FBF7] px-3 py-2 text-sm text-[#1F8F5F]">
            <p className="font-medium">Ticket subtotal: {formatMoney(ticketSubtotalCents)}</p>
            {automaticDiscountCents > 0 && (
              <p className="font-medium">Automatic discounts: -{formatMoney(automaticDiscountCents)}</p>
            )}
            {addOnTotalCents > 0 && (
              <p className="font-medium">Add-ons: +{formatMoney(addOnTotalCents)}</p>
            )}
            <p className="text-base font-semibold">Total due: {totalDisplay}</p>
          </div>
        </aside>
      </div>
  );

  if (isParentLoggedIn) {
    return (
      <ParentPortalShell title="Checkout" subtitle={event.title} showHeaderCard={false}>
        {pageContent}
      </ParentPortalShell>
    );
  }

  return (
    <main className="min-h-screen bg-[#f3f4f8]">
      <PublicClubHeader
        clubName={event.clubProfile?.clubName || 'After-school club'}
        clubSlug={event.clubProfile?.publicSlug}
        clubDescription={event.clubProfile?.description}
        logoUrl={event.clubProfile?.logoUrl}
        showClubTitle={event.clubProfile?.showClubTitle !== false}
        cartHref="/cart"
      />
      {pageContent}
    </main>
  );
};

export default PublicBookingCheckoutPage;


